import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../components/Sidebar";
import { db } from "../firebase/firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, onSnapshot, doc, updateDoc, deleteDoc, collectionGroup } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; 
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { 
  Users, Search, Eye, ShoppingBag, 
  Activity, X, Phone, User as UserIcon, Calendar, 
  FileText, Printer, Building2, TrendingUp, RefreshCw, 
  Filter, FileSpreadsheet, Trash2, Edit, Save, ChevronDown
} from "lucide-react";

const COLORS = ['#16205f', '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#84cc16'];

export default function SuperAdminCustomerMatrix() {
  const [activeBranchId, setActiveBranchId] = useState("ALL"); 
  const [storesList, setStoresList] = useState([]);
  
  // Date Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Cross-Branch Data Dictionaries
  const [allCustomersMap, setAllCustomersMap] = useState({});
  const [allSalesMap, setAllSalesMap] = useState({});
  
  // NEW: Global Prescriptions State based on collectionGroup
  const [globalPrescriptions, setGlobalPrescriptions] = useState([]);
  
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modal / Audit States
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [auditTab, setAuditTab] = useState("profile");
  
  // Edit States
  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  // Dropdown & Preview States
  const [showMatrixDropdown, setShowMatrixDropdown] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

  // --- 1. FETCH ALL STORES ---
  useEffect(() => {
    const auth = getAuth();
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        onSnapshot(collection(db, "stores"), (snapshot) => {
          if (!snapshot.empty) {
            setStoresList(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
          }
        });
      }
    });
    return () => unsubAuth();
  }, []);

  // --- 2. MASS FETCH CUSTOMERS & SALES ---
  useEffect(() => {
    if (storesList.length === 0) return;
    
    const unsubs = [];
    storesList.forEach(store => {
      unsubs.push(onSnapshot(collection(db, "stores", store.id, "customers"), (snap) => {
        setAllCustomersMap(prev => ({ ...prev, [store.id]: snap.docs.map(d => ({ ...d.data(), id: d.id, storeId: store.id })) }));
      }));
      unsubs.push(onSnapshot(collection(db, "stores", store.id, "sales"), (snap) => {
        setAllSalesMap(prev => ({ ...prev, [store.id]: snap.docs.map(d => ({ ...d.data(), id: d.id, storeId: store.id })) }));
      }));
    });

    return () => unsubs.forEach(unsub => unsub());
  }, [storesList]);

  // --- 3. GLOBAL FETCH CLINICAL RECORDS (collectionGroup) ---
  useEffect(() => {
    const unsubPrescriptions = onSnapshot(collectionGroup(db, "prescriptions"), (snapshot) => {
      const allRecords = snapshot.docs.map(d => {
        const parentStoreId = d.ref.parent.parent?.id || "unknown";
        return { id: d.id, parentStoreId, storeId: parentStoreId, ...d.data() };
      });
      setGlobalPrescriptions(allRecords);
    });
    return () => unsubPrescriptions();
  }, []);

  // --- 4. CONSOLIDATION & ANALYTICS ENGINE (WITH FILTERS) ---
  const { consolidatedCustomers, reportMetrics, chartData } = useMemo(() => {
    let rawCustomers = [];
    let rawSales = [];
    let rawRx = [];

    if (activeBranchId === "ALL") {
      Object.values(allCustomersMap).forEach(arr => rawCustomers.push(...arr));
      Object.values(allSalesMap).forEach(arr => rawSales.push(...arr));
      rawRx = globalPrescriptions;
    } else {
      rawCustomers = allCustomersMap[activeBranchId] || [];
      rawSales = allSalesMap[activeBranchId] || [];
      rawRx = globalPrescriptions.filter(rx => rx.parentStoreId === activeBranchId);
    }

    if (startDate && endDate) {
      const start = new Date(startDate).getTime();
      const end = new Date(endDate).getTime() + 86399999;
      
      rawSales = rawSales.filter(s => {
        const t = new Date(s.timestamp).getTime();
        return t >= start && t <= end;
      });
      rawRx = rawRx.filter(rx => {
        const t = new Date(rx.timestamp).getTime();
        return t >= start && t <= end;
      });
    }

    rawSales.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    rawRx.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    const uniqueMap = new Map();
    const branchRevenueMap = {};
    
    rawCustomers.forEach(c => {
      if (!c.phone) return;
      if (!uniqueMap.has(c.phone)) {
        uniqueMap.set(c.phone, { ...c, orderCount: 0, lifetimeValue: 0, theirOrders: [], theirPrescriptions: [], storeIds: new Set([c.storeId]), docIds: [{storeId: c.storeId, id: c.id}] });
      } else {
        const existing = uniqueMap.get(c.phone);
        existing.storeIds.add(c.storeId);
        existing.docIds.push({storeId: c.storeId, id: c.id});
        if (!existing.name && c.name) existing.name = c.name;
        // Merge missing data
        ['address', 'state', 'gstin', 'whatsapp', 'dob', 'anniversary'].forEach(field => {
            if (!existing[field] && c[field]) existing[field] = c[field];
        });
      }
    });

    rawSales.forEach(sale => {
      const phone = sale.customer?.phone;
      if (!phone) return;
      
      if (!uniqueMap.has(phone)) {
        uniqueMap.set(phone, {
          name: sale.customer?.name || "Unknown", phone: phone,
          orderCount: 0, lifetimeValue: 0, theirOrders: [], theirPrescriptions: [], storeIds: new Set([sale.storeId]), docIds: []
        });
      }
      
      const cust = uniqueMap.get(phone);
      const val = (sale.financials?.netPayable || 0);
      cust.theirOrders.push(sale);
      cust.orderCount += 1;
      cust.lifetimeValue += val;
      cust.storeIds.add(sale.storeId);

      const storeName = storesList.find(s => s.id === sale.storeId)?.storeName || "Unknown Branch";
      branchRevenueMap[storeName] = (branchRevenueMap[storeName] || 0) + val;
    });

    rawRx.forEach(rx => {
      const phone = rx.phone || rx.patient?.mobile;
      if (!phone || !uniqueMap.has(phone)) return;
      uniqueMap.get(phone).theirPrescriptions.push(rx);
    });

    let list = Array.from(uniqueMap.values());
    let repeatCustomers = 0;
    let totalRevenue = 0;

    list.forEach(c => {
      if (c.orderCount > 1) repeatCustomers++;
      totalRevenue += c.lifetimeValue;
    });

    const metrics = {
      total: list.length,
      repeatRate: list.length > 0 ? Math.round((repeatCustomers / list.length) * 100) : 0,
      totalRevenue: totalRevenue
    };

    const pieData = Object.keys(branchRevenueMap).map(key => ({
      name: key,
      value: branchRevenueMap[key]
    })).filter(d => d.value > 0);

    if (searchQuery) {
      const term = searchQuery.toLowerCase();
      list = list.filter(c => c.name?.toLowerCase().includes(term) || c.phone?.includes(term));
    }

    list.sort((a, b) => b.lifetimeValue - a.lifetimeValue);

    return { consolidatedCustomers: list, reportMetrics: metrics, chartData: pieData };
  }, [allCustomersMap, allSalesMap, globalPrescriptions, activeBranchId, searchQuery, startDate, endDate, storesList]);

  // --- CRUD OPERATIONS ---
  const handleUpdateCustomer = async () => {
    try {
        for (const docRef of viewingCustomer.docIds) {
            await updateDoc(doc(db, "stores", docRef.storeId, "customers", docRef.id), editFormData);
        }
        setViewingCustomer({ ...viewingCustomer, ...editFormData });
        setEditMode(false);
        alert("Customer updated successfully across all branches.");
    } catch (error) {
        console.error("Error updating:", error);
        alert("Failed to update customer.");
    }
  };

  const handleDeleteCustomer = async (customer) => {
    if(!window.confirm("Are you sure you want to permanently delete this customer from all branches?")) return;
    try {
        for (const docRef of customer.docIds) {
            await deleteDoc(doc(db, "stores", docRef.storeId, "customers", docRef.id));
        }
        setViewingCustomer(null);
    } catch (error) {
        console.error("Error deleting:", error);
    }
  };

  const handleDeleteOrder = async (orderId, storeId) => {
    if(!window.confirm("Delete this order record?")) return;
    try {
        await deleteDoc(doc(db, "stores", storeId, "sales", orderId));
        setViewingCustomer(prev => ({
            ...prev,
            theirOrders: prev.theirOrders.filter(o => o.id !== orderId)
        }));
    } catch (error) { console.error("Error deleting order:", error); }
  };

  const handleDeleteRx = async (rxId, storeId) => {
    if(!window.confirm("Delete this clinical record?")) return;
    try {
        await deleteDoc(doc(db, "stores", storeId, "prescriptions", rxId));
        setViewingCustomer(prev => ({
            ...prev,
            theirPrescriptions: prev.theirPrescriptions.filter(r => r.id !== rxId)
        }));
    } catch (error) { console.error("Error deleting rx:", error); }
  };

  // --- EXPORT FUNCTIONS (PDF / Excel) ---
  const handleExportMatrixPDF = (action = 'export') => {
    const doc = new jsPDF('p', 'pt', 'a4');
    const scopeName = activeBranchId === "ALL" ? "Global Network Scope" : (storesList.find(s => s.id === activeBranchId)?.storeName || activeBranchId);
    const dateStr = new Date().toLocaleDateString();
    
    doc.setFillColor(22, 32, 95); 
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), 80, 'F');
    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.text(`NETWORK CONSOLIDATION REPORT`, 40, 45);
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(200, 200, 200);
    doc.text(`Scope: ${scopeName}   |   Date: ${dateStr}   |   Filter: ${startDate && endDate ? `${startDate} to ${endDate}` : 'All Time'}`, 40, 65);
    
    let currentY = 110;

    doc.setTextColor(40, 40, 40);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Executive Summary", 40, currentY);
    currentY += 20;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`Total Network Clients: ${reportMetrics.total.toLocaleString()}`, 40, currentY);
    doc.text(`Total Gross Revenue: INR ${reportMetrics.totalRevenue.toLocaleString()}`, 250, currentY);
    doc.text(`Client Repeat Rate: ${reportMetrics.repeatRate}%`, 450, currentY);
    currentY += 40;

    if (chartData.length > 0) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Branch Revenue Distribution", 40, currentY);
      currentY += 15;

      const branchCols = ["Branch / Store Name", "Total Revenue Generated (INR)"];
      const branchRows = chartData.sort((a,b)=>b.value-a.value).map(d => [d.name, `₹${d.value.toLocaleString()}`]);

      autoTable(doc, {
        head: [branchCols],
        body: branchRows,
        startY: currentY,
        theme: 'grid',
        styles: { fontSize: 10, cellPadding: 6, textColor: [40,40,40] },
        headStyles: { fillColor: [16, 185, 129], textColor: [255,255,255], fontStyle: 'bold' }, 
        alternateRowStyles: { fillColor: [248, 250, 252] }
      });
      currentY = doc.lastAutoTable.finalY + 40;
    }

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Complete Client Matrix", 40, currentY);
    currentY += 15;

    const tableColumn = ["Client Profile Name", "Mobile Contact", "Total Orders", "Lifetime Value (INR)", "Branches Visited"];
    const tableRows = consolidatedCustomers.map(c => [
      c.name || "Unknown",
      c.phone || "-",
      c.orderCount,
      c.lifetimeValue.toLocaleString(),
      Array.from(c.storeIds).map(id => storesList.find(s => s.id === id)?.storeName || id).join(", ")
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: currentY,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 5, textColor: [40,40,40] },
      headStyles: { fillColor: [22, 32, 95], textColor: [255,255,255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] }
    });

    if (action === 'preview') {
      const pdfDataUrl = doc.output('datauristring');
      setPdfPreviewUrl(pdfDataUrl);
    } else {
      doc.save(`Global_Matrix_Report_${dateStr.replace(/\//g, '-')}.pdf`);
    }
  };

  // --- COMPREHENSIVE COMBINED LEDGER PDF EXPORT ---
  const handleExportLedgerPDF = (customer) => {
    if (!customer) return;
    const doc = new jsPDF('p', 'pt', 'a4');
    const dateStr = new Date().toLocaleDateString();
    
    // Header
    doc.setFillColor(22, 32, 95); 
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), 90, 'F');
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.text(`COMPLETE CLIENT AUDIT LEDGER`, 40, 45);
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(200, 200, 200);
    doc.text(`Generated: ${dateStr}   |   Lifetime Value: INR ${customer.lifetimeValue.toLocaleString()}   |   Orders: ${customer.orderCount}`, 40, 65);
    
    let startY = 120;

    // SECTION 1: Identity & Routing
    doc.setTextColor(22, 32, 95);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Identity & Routing Information", 40, startY);
    startY += 15;

    const identityBody = [
        ['Full Name', customer.name || '-', 'Mobile', customer.phone || '-'],
        ['Address', customer.address || '-', 'State', customer.state || '-'],
        ['GSTIN', customer.gstin || '-', 'WhatsApp', customer.whatsapp || '-'],
        ['DOB', customer.dob || '-', 'Anniversary', customer.anniversary || '-']
    ];

    autoTable(doc, {
        body: identityBody,
        startY: startY,
        theme: 'grid',
        styles: { fontSize: 10, cellPadding: 6, textColor: [40, 40, 40] },
        columnStyles: {
            0: { fontStyle: 'bold', fillColor: [245, 247, 250] },
            2: { fontStyle: 'bold', fillColor: [245, 247, 250] }
        }
    });
    startY = doc.lastAutoTable.finalY + 30;

    // SECTION 2: Billing Ledger
    doc.setTextColor(22, 32, 95);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Billing Ledger (Sales History)", 40, startY);
    startY += 15;

    if (customer.theirOrders && customer.theirOrders.length > 0) {
      const orderCols = ["Date", "Invoice #", "Status", "Items Purchased", "Net Paid (INR)"];
      const orderRows = customer.theirOrders.map(o => [
        o.timestamp?.split("T")[0] || "-",
        o.invoiceNo || "-",
        o.orderStatus || "-",
        o.items?.map(i => `${i.productName} (x${i.quantity})`).join(",\n") || "-",
        o.financials?.netPayable?.toLocaleString() || "0"
      ]);

      autoTable(doc, {
        head: [orderCols], body: orderRows, startY: startY, theme: 'grid',
        styles: { fontSize: 9, cellPadding: 5 }, headStyles: { fillColor: [16, 185, 129] }
      });
      startY = doc.lastAutoTable.finalY + 30;
    } else {
      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(100, 100, 100);
      doc.text("No sales history logged for this client.", 40, startY);
      startY += 30;
    }

    // SECTION 3: Clinical Records
    if (startY > doc.internal.pageSize.getHeight() - 150) { doc.addPage(); startY = 40; }
    
    doc.setTextColor(22, 32, 95);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Clinical Records (Prescriptions)", 40, startY);
    startY += 15;

    if (customer.theirPrescriptions && customer.theirPrescriptions.length > 0) {
      const rxCols = ["Date / Optometrist", "OD (Right Eye)\nSPH / CYL / AXIS", "OS (Left Eye)\nSPH / CYL / AXIS", "Notes"];
      const rxRows = customer.theirPrescriptions.map(rx => [
        `${rx.timestamp?.split("T")[0] || "-"}\nBy: ${rx.doctorName || rx.doctor?.name || "-"}`,
        `DV: ${rx.sphRight||'-'} / ${rx.cylRight||'-'} / ${rx.axisRight||'-'}\nNV: ${rx.nvSphRight||'-'} / ${rx.nvCylRight||'-'} / ${rx.nvAxisRight||'-'}\nADD: ${rx.addRight||'-'}`,
        `DV: ${rx.sphLeft||'-'} / ${rx.cylLeft||'-'} / ${rx.axisLeft||'-'}\nNV: ${rx.nvSphLeft||'-'} / ${rx.nvCylLeft||'-'} / ${rx.nvAxisLeft||'-'}\nADD: ${rx.addLeft||'-'}`,
        rx.prescriptionNotes || "-"
      ]);

      autoTable(doc, {
        head: [rxCols], body: rxRows, startY: startY, theme: 'grid',
        styles: { fontSize: 9, cellPadding: 5 }, headStyles: { fillColor: [59, 130, 246] }
      });
    } else {
      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(100, 100, 100);
      doc.text("No clinical measurements recorded for this client.", 40, startY);
    }

    doc.save(`${customer.name || 'Client'}_Full_Ledger_${dateStr.replace(/\//g, '-')}.pdf`);
  };

  const handleExportGlobalExcel = () => {
    const workbook = XLSX.utils.book_new();

    const summaryData = [
      { "Metric": "Report Generation Date", "Value": new Date().toLocaleDateString() },
      { "Metric": "Selected Scope", "Value": activeBranchId === "ALL" ? "Global" : activeBranchId },
      { "Metric": "Total Network Clients", "Value": reportMetrics.total },
      { "Metric": "Total Gross Revenue (INR)", "Value": reportMetrics.totalRevenue },
      { "Metric": "Client Repeat Rate (%)", "Value": reportMetrics.repeatRate },
      { "Metric": "", "Value": "" }, 
      { "Metric": "--- BRANCH REVENUE BREAKDOWN ---", "Value": "---" }
    ];
    
    chartData.sort((a,b)=>b.value-a.value).forEach(branch => {
      summaryData.push({ "Metric": `Branch: ${branch.name}`, "Value": branch.value });
    });

    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summaryWs, "Executive Summary");

    const exportData = consolidatedCustomers.map(c => ({
      "Client Profile Name": c.name || "Unknown",
      "Mobile Contact Number": c.phone || "N/A",
      "Total Lifetime Value (INR)": c.lifetimeValue,
      "Total Order Count": c.orderCount,
      "Store Branches Visited": Array.from(c.storeIds).map(id => storesList.find(s => s.id === id)?.storeName || id).join(", "),
      "City/State": c.state || "N/A"
    }));
    const matrixWs = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(workbook, matrixWs, "Customer Matrix");

    XLSX.writeFile(workbook, `Global_Customer_Matrix_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleExportLedgerExcel = (customer) => {
    if (!customer || !customer.theirOrders || customer.theirOrders.length === 0) {
      alert("No sales history to export.");
      return;
    }
    const data = customer.theirOrders.map(order => ({
      "Invoice No": order.invoiceNo,
      "Date": order.timestamp?.split("T")[0] || "-",
      "Branch": order.branch || "-",
      "Status": order.orderStatus,
      "Net Paid (INR)": order.financials?.netPayable || 0,
      "Items Purchased": order.items?.map(i => `${i.productName} (x${i.quantity})`).join(" | ") || "-"
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Client Ledger");
    XLSX.writeFile(wb, `${customer.name || 'Client'}_Ledger.xlsx`);
  };

  const handleOpenAudit = (customer) => {
    setViewingCustomer(customer);
    setEditFormData({
        name: customer.name || "", phone: customer.phone || "", address: customer.address || "", 
        state: customer.state || "", gstin: customer.gstin || "", whatsapp: customer.whatsapp || "", 
        dob: customer.dob || "", anniversary: customer.anniversary || ""
    });
    setAuditTab("profile");
    setEditMode(false);
  };

  const handlePrintFullAudit = (customer) => {
    const dateStr = new Date().toLocaleDateString();
    const primaryStoreId = Array.from(customer.storeIds || [])[0];
    const storeObj = storesList.find(s => s.id === primaryStoreId) || storesList[0] || {};
    
    let ordersHtml = customer.theirOrders?.map(order => `
        <div style="padding: 15px; border-bottom: 1px dashed #ddd;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 13px;">
            <div><strong>Invoice:</strong> #${order.invoiceNo} <span style="color:#666; font-size:11px;">(${order.branch || 'Branch'})</span></div>
            <div><strong>Date:</strong> ${order.timestamp?.split("T")[0] || '-'}</div>
          </div>
          <table style="margin-bottom: 10px;">
            <tr><th style="text-align: left;">Item Description</th><th>Qty</th><th style="text-align: right;">Total</th></tr>
            ${order.items?.map(item => `<tr><td style="text-align: left;">${item.productName}</td><td>${item.quantity}</td><td style="text-align: right;">₹${(item.retailPrice * item.quantity).toLocaleString()}</td></tr>`).join('')}
          </table>
          <div style="display: flex; justify-content: space-between; font-size: 13px;">
            <span style="color: ${order.orderStatus === 'Pending' ? '#d97706' : '#059669'}; font-weight: bold; text-transform: uppercase;">${order.orderStatus}</span>
            <strong style="font-size: 15px; color: #16205f;">Net Paid: ₹${order.financials?.netPayable?.toLocaleString() || '0'}</strong>
          </div>
        </div>
      `).join('') || `<div style="padding: 15px; text-align: center;">No sales history logged.</div>`;

    let rxHtml = customer.theirPrescriptions?.map(record => `
        <div style="padding: 15px; border-bottom: 1px dashed #ddd;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 13px;">
            <div><strong>Date:</strong> ${record.timestamp ? record.timestamp.split("T")[0] : "-"}</div>
            <div><strong>Optometrist:</strong> ${record.doctor?.name || record.doctorName || 'Assigned Doctor'}</div>
          </div>
          <table>
            <tr><th rowspan="2">EYE</th><th colspan="4">DISTANCE VISION (DV)</th><th colspan="3">NEAR VISION (NV)</th><th rowspan="2">ADD</th></tr>
            <tr><th>SPH</th><th>CYL</th><th>AXIS</th><th>VA</th><th>SPH</th><th>CYL</th><th>AXIS</th></tr>
            <tr><td>OD</td><td>${record.sphRight||'-'}</td><td>${record.cylRight||'-'}</td><td>${record.axisRight||'-'}</td><td>${record.vaRight||'-'}</td><td>${record.nvSphRight||'-'}</td><td>${record.nvCylRight||'-'}</td><td>${record.nvAxisRight||'-'}</td><td style="color:red;">${record.addRight||'-'}</td></tr>
            <tr><td>OS</td><td>${record.sphLeft||'-'}</td><td>${record.cylLeft||'-'}</td><td>${record.axisLeft||'-'}</td><td>${record.vaLeft||'-'}</td><td>${record.nvSphLeft||'-'}</td><td>${record.nvCylLeft||'-'}</td><td>${record.nvAxisLeft||'-'}</td><td style="color:red;">${record.addLeft||'-'}</td></tr>
          </table>
        </div>
      `).join('') || `<div style="padding: 15px; text-align: center;">No clinical measurements.</div>`;

    const htmlContent = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
    <meta charset="UTF-8">
    <title>Full Audit Report - ${customer.name || 'Client'}</title>
    <style>
      * { margin:0; padding:0; box-sizing:border-box; font-family: 'Segoe UI', Arial, sans-serif; }
      @page { size: A4; margin: 15mm; }
      body { background: #fff; color: #222; -webkit-print-color-adjust: exact; }
      .header { text-align: center; border-bottom: 2px solid #16205f; padding-bottom: 15px; margin-bottom: 20px; }
      .clinic-name { font-size: 24px; font-weight: bold; color: #16205f; }
      .patient-section { display: flex; gap: 20px; margin-bottom: 20px; }
      .info-box { border: 1px solid #ddd; padding: 15px; flex: 1; background: #fafafa; }
      .info-row { display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 13px; }
      .section { border: 1px solid #ddd; margin-bottom: 25px; }
      .section-title { background: #f0f4f8; padding: 10px; font-size: 14px; font-weight: bold; border-bottom: 1px solid #ddd; }
      table { width: 100%; border-collapse: collapse; text-align: center; font-size: 12px; }
      th, td { padding: 8px; border: 1px solid #ddd; }
      th { background: #fafafa; }
    </style>
    </head>
    <body>
        <div class="header">
          <div class="clinic-name">${storeObj.storeName || "Store"}</div>
          <div>Audit Report</div>
        </div>
        <div class="patient-section">
          <div class="info-box">
            <h4>Identity</h4>
            <div class="info-row"><span>Name:</span> <strong>${customer.name || '-'}</strong></div>
            <div class="info-row"><span>Mobile:</span> <strong>${customer.phone || '-'}</strong></div>
            <div class="info-row"><span>Address:</span> <strong>${customer.address || '-'}</strong></div>
            <div class="info-row"><span>GSTIN:</span> <strong>${customer.gstin || '-'}</strong></div>
          </div>
          <div class="info-box">
            <h4>Routing</h4>
            <div class="info-row"><span>State:</span> <strong>${customer.state || '-'}</strong></div>
            <div class="info-row"><span>DOB:</span> <strong>${customer.dob || '-'}</strong></div>
            <div class="info-row"><span>Anniversary:</span> <strong>${customer.anniversary || '-'}</strong></div>
            <div class="info-row"><span>WhatsApp:</span> <strong>${customer.whatsapp || '-'}</strong></div>
          </div>
        </div>
        <div class="section"><div class="section-title">Billing Ledger</div>${ordersHtml}</div>
        <div class="section"><div class="section-title">Clinical Records</div>${rxHtml}</div>
      <script>window.onload = () => { setTimeout(() => { window.print(); window.close(); }, 300); }</script>
    </body>
    </html>`;
    
    const printWindow = window.open('', '_blank', 'width=800,height=900');
    if (printWindow) { printWindow.document.open(); printWindow.document.write(htmlContent); printWindow.document.close(); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-800 font-sans text-base">
      <Sidebar />

      <div className="flex-1 p-6 lg:p-10 space-y-6 overflow-x-hidden">
        
        {/* HEADER & FILTERS */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
          <div>
            <h2 className="text-2xl font-black text-slate-800 uppercase">Global Analytics Matrix</h2>
            <p className="text-slate-500 text-sm font-medium">Consolidated multi-branch reporting and exports.</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 gap-2">
              <Calendar size={16} className="text-blue-500"/>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent text-sm outline-none font-bold text-slate-700"/>
              <span className="text-slate-400 font-bold">to</span>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent text-sm outline-none font-bold text-slate-700"/>
              {(startDate || endDate) && (
                <button onClick={() => {setStartDate(""); setEndDate("");}} className="text-rose-500 hover:text-rose-700"><X size={16}/></button>
              )}
            </div>
            
            <div className="relative">
              <button onClick={() => setShowMatrixDropdown(!showMatrixDropdown)} className="bg-rose-50 hover:bg-rose-600 text-rose-700 hover:text-white border border-rose-200 px-4 py-2.5 rounded-xl transition-all font-black text-[10px] uppercase tracking-wider flex items-center gap-2 shadow-sm">
                <FileText size={16} /> Matrix Report (PDF) <ChevronDown size={14} />
              </button>
              {showMatrixDropdown && (
                <div className="absolute top-full mt-2 right-0 bg-white border border-slate-200 shadow-xl rounded-xl overflow-hidden z-40 flex flex-col w-full min-w-[150px]">
                  <button onClick={() => { setShowMatrixDropdown(false); handleExportMatrixPDF('preview'); }} className="px-4 py-3 text-left text-xs font-black uppercase text-slate-700 hover:bg-slate-50 border-b border-slate-100 flex items-center gap-2">
                    <Eye size={14}/> Preview
                  </button>
                  <button onClick={() => { setShowMatrixDropdown(false); handleExportMatrixPDF('export'); }} className="px-4 py-3 text-left text-xs font-black uppercase text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                    <FileText size={14}/> Export
                  </button>
                </div>
              )}
            </div>
            <button onClick={handleExportGlobalExcel} className="bg-emerald-50 hover:bg-emerald-600 text-emerald-700 hover:text-white border border-emerald-200 px-4 py-2.5 rounded-xl transition-all font-black text-[10px] uppercase tracking-wider flex items-center gap-2 shadow-sm">
              <FileSpreadsheet size={16} /> Matrix Report (Excel)
            </button>
          </div>
        </div>

        {/* BRANCH SELECTOR */}
        <div className="bg-white border border-slate-200 rounded-3xl p-4 shadow-sm">
           <h3 className="text-xs font-black uppercase text-slate-500 mb-3 flex items-center gap-2 px-2">
             <Building2 size={16}/> Target Data Scope
           </h3>
           <div className="flex overflow-x-auto gap-4 pb-2 hide-scrollbar">
             <button
                 onClick={() => setActiveBranchId("ALL")}
                 className={`min-w-[220px] p-4 rounded-2xl border transition-all text-left flex flex-col justify-between ${
                   activeBranchId === "ALL" 
                     ? 'bg-slate-900 text-white shadow-lg shadow-slate-300 border-slate-900' 
                     : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-white'
                 }`}
               >
                 <h3 className="font-black text-sm uppercase truncate w-full flex items-center gap-2">
                   <Activity size={16} className={activeBranchId === "ALL" ? "text-emerald-400" : "text-emerald-600"}/> 
                   Global Scope
                 </h3>
             </button>
             {storesList.map(store => (
               <button
                 key={store.id}
                 onClick={() => setActiveBranchId(store.id)}
                 className={`min-w-[220px] p-4 rounded-2xl border transition-all text-left flex flex-col justify-between ${
                   activeBranchId === store.id 
                     ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 border-blue-600' 
                     : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-blue-300 hover:bg-white'
                 }`}
               >
                 <h3 className="font-black text-sm uppercase truncate w-full">{store.storeName || store.id}</h3>
                 <p className={`text-[10px] mt-1 font-medium truncate w-full ${activeBranchId === store.id ? 'text-blue-200' : 'text-slate-400'}`}>
                   {store.address || 'Location Unspecified'}
                 </p>
               </button>
             ))}
           </div>
        </div>

        {/* METRICS & ADDON REPORTS */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-1 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
            <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm relative overflow-hidden group">
              <div className="absolute right-[-20px] top-[-20px] opacity-5 group-hover:scale-110 transition-transform duration-500"><Users size={100}/></div>
              <span className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-1 block">Total Network Clients</span>
              <span className="text-3xl font-black text-slate-800 font-mono">{reportMetrics.total.toLocaleString()}</span>
            </div>

            <div className="bg-emerald-50 p-5 rounded-3xl border border-emerald-100 shadow-sm relative overflow-hidden group">
              <div className="absolute right-[-20px] top-[-20px] opacity-10 group-hover:scale-110 transition-transform duration-500 text-emerald-600"><TrendingUp size={100}/></div>
              <span className="text-[10px] uppercase font-black tracking-widest text-emerald-600 mb-1 block">Total Revenue (Gross)</span>
              <span className="text-3xl font-black text-emerald-800 font-mono">₹{reportMetrics.totalRevenue.toLocaleString()}</span>
            </div>
            
            <div className="bg-purple-50 p-5 rounded-3xl border border-purple-100 shadow-sm relative overflow-hidden group sm:col-span-2 xl:col-span-1">
              <div className="absolute right-[-20px] top-[-20px] opacity-10 group-hover:scale-110 transition-transform duration-500 text-purple-600"><RefreshCw size={100}/></div>
              <span className="text-[10px] uppercase font-black tracking-widest text-purple-600 mb-1 block">Repeat Customer Rate</span>
              <span className="text-3xl font-black text-purple-800 font-mono">{reportMetrics.repeatRate}%</span>
            </div>
          </div>

          <div className="xl:col-span-2 bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-6 items-center">
            <div className="flex-1 w-full h-[250px] relative">
              <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2 text-center">Revenue Distribution</h3>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={chartData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div className="flex-1 w-full bg-slate-50 rounded-2xl p-5 border border-slate-100 h-full max-h-[250px] overflow-y-auto">
               <h3 className="text-[10px] font-black uppercase text-slate-500 tracking-widest mb-4 flex items-center gap-2 border-b border-slate-200 pb-2"><Filter size={14}/> Top Performing Branches (All)</h3>
               <div className="space-y-4 pr-2">
                 {chartData.sort((a,b) => b.value - a.value).map((d, i) => (
                   <div key={i} className="flex justify-between items-center border-b border-slate-200/50 pb-3 last:border-0 last:pb-0">
                     <span className="text-sm font-bold text-slate-700 truncate mr-4">{d.name}</span>
                     <span className="text-sm font-mono font-black text-emerald-600 shrink-0">₹{d.value.toLocaleString()}</span>
                   </div>
                 ))}
                 {chartData.length === 0 && <span className="text-slate-400 font-bold text-sm block text-center py-4">No revenue data for selected criteria.</span>}
               </div>
            </div>
          </div>
        </div>

        {/* SEARCH BAR */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex items-center gap-3">
          <div className="flex flex-1 items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 focus-within:border-blue-500 focus-within:bg-white transition-colors">
            <Search className="text-blue-500" size={20} />
            <input 
              type="text" 
              placeholder="Search by customer name or registered phone number..." 
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none w-full text-sm py-1.5 text-slate-800 font-bold" 
            />
          </div>
        </div>

        {/* CUSTOMER DIRECTORY TABLE */}
        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left whitespace-nowrap">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200 text-slate-500 text-[10px] uppercase tracking-widest font-black">
                  <th className="p-5 pl-6">Client Profile</th>
                  <th className="p-5">Mobile</th>
                  <th className="p-5 text-right">LTV (₹)</th>
                  <th className="p-5 text-center">Orders</th>
                  <th className="p-5 pr-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium text-sm">
                {consolidatedCustomers.length === 0 ? (
                  <tr><td colSpan="5" className="p-10 text-center text-slate-400 font-bold">No records located.</td></tr>
                ) : (
                  consolidatedCustomers.map((c, i) => (
                    <tr key={i} className="hover:bg-slate-50/70 transition-colors">
                      <td className="p-4 pl-6 font-black text-slate-800">{c.name || "Unknown"}</td>
                      <td className="p-4 font-mono text-slate-600 font-bold">{c.phone || "-"}</td>
                      <td className="p-4 text-right font-mono font-black text-emerald-600">₹{c.lifetimeValue.toLocaleString()}</td>
                      <td className="p-4 text-center">
                        <span className={`border px-3 py-1 rounded-lg font-black text-xs ${c.orderCount > 1 ? 'bg-purple-50 text-purple-600 border-purple-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                          {c.orderCount} {c.orderCount > 1 ? '⭐' : ''}
                        </span>
                      </td>
                      <td className="p-4 pr-6 text-right flex justify-end gap-2">
                        <button onClick={() => handleOpenAudit(c)} className="px-4 py-2 bg-slate-50 hover:bg-slate-800 text-slate-600 hover:text-white border border-slate-200 rounded-xl transition-all font-black text-[10px] uppercase flex items-center gap-2">
                          <Eye size={14} /> View Audit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* FULL-SCREEN AUDIT MODAL OVERLAY */}
      <AnimatePresence>
        {viewingCustomer && (
          <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/60 backdrop-blur-sm p-2 lg:p-0">
            <motion.div 
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} 
              transition={{ type: "spring", stiffness: 120, damping: 25 }}
              className="w-full md:w-[750px] lg:w-[900px] h-full bg-white md:rounded-l-3xl shadow-2xl border-l border-slate-200 flex flex-col overflow-hidden"
            >
              <div className="p-6 lg:p-8 border-b border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center bg-slate-50/50 gap-4">
                <div>
                  <h2 className="text-2xl font-black text-slate-800 uppercase tracking-wider">{viewingCustomer.name || "CLIENT"}</h2>
                  <p className="text-blue-600 font-mono mt-1 text-sm font-bold flex items-center gap-2"><Phone size={16}/> {viewingCustomer.phone}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => handleExportLedgerPDF(viewingCustomer)} className="p-3 bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-600 hover:text-white rounded-2xl transition-colors shadow-sm flex items-center gap-2 text-xs uppercase font-black px-4">
                    <FileText size={16} /> Save PDF
                  </button>
                  <button onClick={() => handlePrintFullAudit(viewingCustomer)} className="p-3 bg-blue-600 text-white hover:bg-blue-700 rounded-2xl transition-colors shadow-sm flex items-center gap-2 text-xs uppercase font-black px-4">
                    <Printer size={16} /> Print Full
                  </button>
                  <button onClick={() => handleDeleteCustomer(viewingCustomer)} className="p-3 bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white rounded-2xl transition-colors shadow-sm flex items-center gap-2 text-xs uppercase font-black px-4">
                    <Trash2 size={16} /> Delete All
                  </button>
                  <button onClick={() => setViewingCustomer(null)} className="p-3 bg-white border border-slate-200 hover:bg-rose-50 rounded-2xl transition-colors text-slate-500 shadow-sm ml-2">
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex bg-slate-50/50 px-6 lg:px-8 pt-2 gap-2 border-b border-slate-200">
                <button onClick={() => setAuditTab("profile")} className={`px-6 py-3.5 font-black text-[10px] uppercase rounded-t-2xl border-t border-l border-r border-transparent ${auditTab === "profile" ? "bg-white text-blue-600 border-slate-200 !border-b-white translate-y-[1px]" : "text-slate-500 hover:bg-slate-100"}`}>
                  <UserIcon size={14} className="inline mr-1.5" /> Identity & Routing
                </button>
                <button onClick={() => setAuditTab("orders")} className={`px-6 py-3.5 font-black text-[10px] uppercase rounded-t-2xl border-t border-l border-r border-transparent ${auditTab === "orders" ? "bg-white text-blue-600 border-slate-200 !border-b-white translate-y-[1px]" : "text-slate-500 hover:bg-slate-100"}`}>
                  <ShoppingBag size={14} className="inline mr-1.5" /> Billing Ledger
                </button>
                <button onClick={() => setAuditTab("prescriptions")} className={`px-6 py-3.5 font-black text-[10px] uppercase rounded-t-2xl border-t border-l border-r border-transparent ${auditTab === "prescriptions" ? "bg-white text-blue-600 border-slate-200 !border-b-white translate-y-[1px]" : "text-slate-500 hover:bg-slate-100"}`}>
                  <Activity size={14} className="inline mr-1.5" /> Clinical Records
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 lg:p-8 bg-white space-y-8">
                
                {/* IDENTITY TAB */}
                {auditTab === 'profile' && (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest">Client Demographics</h3>
                        <button onClick={() => setEditMode(!editMode)} className="flex items-center gap-2 text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100">
                            {editMode ? <X size={14} /> : <Edit size={14} />} {editMode ? 'Cancel Edit' : 'Edit Info'}
                        </button>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 shadow-sm">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-5 gap-x-6">
                        {['name', 'phone', 'address', 'state', 'gstin', 'whatsapp', 'dob', 'anniversary'].map((field) => (
                            <div key={field}>
                                <span className="block text-[10px] uppercase text-slate-400 font-black mb-1">{field}</span>
                                {editMode ? (
                                    <input 
                                        type={field === 'dob' || field === 'anniversary' ? 'date' : 'text'}
                                        value={editFormData[field]} 
                                        onChange={(e) => setEditFormData({...editFormData, [field]: e.target.value})}
                                        className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm font-bold text-slate-800 focus:border-blue-500 focus:outline-none"
                                    />
                                ) : (
                                    <span className={`font-bold text-slate-800 text-sm ${field === 'phone' || field === 'whatsapp' ? 'font-mono' : ''}`}>
                                        {viewingCustomer[field] || "-"}
                                    </span>
                                )}
                            </div>
                        ))}
                      </div>
                      
                      {editMode && (
                          <div className="mt-6 pt-6 border-t border-slate-200 flex justify-end">
                              <button onClick={handleUpdateCustomer} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-black text-xs uppercase transition-colors">
                                  <Save size={16} /> Save Changes
                              </button>
                          </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ORDERS TAB */}
                {auditTab === 'orders' && (
                  <div className="space-y-5">
                    {viewingCustomer.theirOrders?.length === 0 ? (
                      <div className="text-center text-slate-400 font-bold p-10 bg-slate-50 border border-slate-200 rounded-3xl">No sales history logged.</div>
                    ) : (
                      viewingCustomer.theirOrders.map(order => (
                        <div key={order.id} className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm relative group">
                          <button onClick={() => handleDeleteOrder(order.id, order.storeId)} className="absolute top-6 right-6 text-rose-400 hover:text-rose-600 hover:bg-rose-50 p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                              <Trash2 size={16}/>
                          </button>
                          <div className="flex justify-between border-b border-slate-100 pb-4 mb-4 pr-10">
                            <div><span className="text-[10px] uppercase tracking-wider text-slate-400 font-black block mb-1">Invoice Code</span><span className="text-lg font-black text-blue-600 font-mono">#{order.invoiceNo}</span></div>
                            <div className="text-right"><span className="text-[10px] uppercase tracking-wider text-slate-400 font-black block mb-1">Transaction Date</span><span className="font-mono text-slate-800 font-bold">{order.timestamp?.split("T")[0]}</span></div>
                          </div>
                          <div className="bg-slate-50 p-4 rounded-2xl mb-4 border border-slate-200">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm py-2 border-b border-slate-200/60 last:border-0">
                                <span className="text-slate-800 font-bold">{item.productName} <span className="text-slate-400 font-normal">x{item.quantity}</span></span>
                                <span className="font-mono font-bold text-slate-600">₹{(item.retailPrice * item.quantity).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-between items-end">
                            <span className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest ${order.orderStatus === 'Pending' ? 'bg-amber-50 text-amber-600 border border-amber-200' : 'bg-emerald-50 text-emerald-600 border border-emerald-200'}`}>{order.orderStatus}</span>
                            <span className="text-2xl font-black text-slate-800 font-mono">₹{order.financials?.netPayable?.toLocaleString()}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* PRESCRIPTIONS TAB */}
                {auditTab === 'prescriptions' && (
                  <div className="space-y-5">
                    {viewingCustomer.theirPrescriptions?.length === 0 ? (
                      <div className="text-center text-slate-400 font-bold p-10 bg-slate-50 border border-slate-200 rounded-3xl">No clinical measurements recorded.</div>
                    ) : (
                      viewingCustomer.theirPrescriptions.map(rx => (
                        <div key={rx.id} className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm relative group">
                          <button onClick={() => handleDeleteRx(rx.id, rx.storeId)} className="absolute top-6 right-6 text-rose-400 hover:text-rose-600 hover:bg-rose-50 p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                              <Trash2 size={16}/>
                          </button>
                          <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-4 mb-4 pr-10">
                            <span className="font-black text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5"><Calendar size={14}/> Recorded On</span>
                            <span className="font-mono text-slate-800 font-black text-base">{rx.timestamp ? rx.timestamp.split("T")[0] : "-"}</span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                                <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest block mb-3">Right Eye (OD)</span>
                                <div className="text-sm font-mono text-slate-800 font-bold flex flex-col gap-2">
                                    <div className="flex justify-between border-b border-slate-200/50 pb-2"><span className="text-slate-400">SPH</span> <span className="text-slate-800">{rx.sphRight || "-"}</span></div>
                                    <div className="flex justify-between border-b border-slate-200/50 pb-2"><span className="text-slate-400">CYL</span> <span className="text-slate-800">{rx.cylRight || "-"}</span></div>
                                    <div className="flex justify-between border-b border-slate-200/50 pb-2"><span className="text-slate-400">AXIS</span> <span className="text-slate-800">{rx.axisRight || "-"}</span></div>
                                    <div className="flex justify-between text-rose-600"><span className="text-slate-400">ADD</span> <span>{rx.addRight || "-"}</span></div>
                                </div>
                            </div>
                            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                                <span className="text-[10px] font-black text-purple-600 uppercase tracking-widest block mb-3">Left Eye (OS)</span>
                                <div className="text-sm font-mono text-slate-800 font-bold flex flex-col gap-2">
                                    <div className="flex justify-between border-b border-slate-200/50 pb-2"><span className="text-slate-400">SPH</span> <span className="text-slate-800">{rx.sphLeft || "-"}</span></div>
                                    <div className="flex justify-between border-b border-slate-200/50 pb-2"><span className="text-slate-400">CYL</span> <span className="text-slate-800">{rx.cylLeft || "-"}</span></div>
                                    <div className="flex justify-between border-b border-slate-200/50 pb-2"><span className="text-slate-400">AXIS</span> <span className="text-slate-800">{rx.axisLeft || "-"}</span></div>
                                    <div className="flex justify-between text-rose-600"><span className="text-slate-400">ADD</span> <span>{rx.addLeft || "-"}</span></div>
                                </div>
                            </div>
                          </div>
                          {rx.prescriptionNotes && (
                            <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100 text-sm">
                              <span className="text-[10px] uppercase font-black tracking-widest text-blue-500 block mb-1">Optometrist Notes</span>
                              <span className="text-blue-900 font-bold leading-relaxed">{rx.prescriptionNotes}</span>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* PDF PREVIEW MODAL */}
      <AnimatePresence>
        {pdfPreviewUrl && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              animate={{ opacity: 1, scale: 1 }} 
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-4xl h-[85vh] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden"
            >
              <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                <h3 className="font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
                  <Eye size={18}/> Matrix Report Preview
                </h3>
                <div className="flex gap-2">
                   <button 
                     onClick={() => { 
                       setPdfPreviewUrl(null); 
                       handleExportMatrixPDF('export'); 
                     }} 
                     className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase flex items-center gap-2"
                   >
                      <FileText size={14}/> Export Now
                   </button>
                   <button 
                     onClick={() => setPdfPreviewUrl(null)} 
                     className="p-2 bg-white border border-slate-200 hover:bg-rose-50 text-slate-500 hover:text-rose-600 rounded-xl transition-colors"
                   >
                     <X size={20} />
                   </button>
                </div>
              </div>
              <div className="flex-1 bg-slate-200">
                <iframe src={pdfPreviewUrl} className="w-full h-full border-none" title="PDF Preview"></iframe>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}